﻿import { toast } from 'sonner';
import i18next from 'i18next';
import { useCorrectiveActionStore } from '../../store/corrective-action.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CorrectiveActionIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchStats() {
        try {
            const response = await this.service.getStats();
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    }

    /**
     * @param {Record<string, unknown>} columnFilters
     * @param {{ pageKey?: string }} [listContext] - Use `pageKey: 'auditComplianceCapa'` on CAPA route (permissions + pending create rows)
     */
    async fetchCorrectiveActions(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}, listContext = {}) {
        const store = useCorrectiveActionStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...(listContext?.pageKey ? { pageKey: listContext.pageKey } : {}),
                ...mapColumnFiltersForQuery(columnFilters)
            });

            const { data, pagination, uniqueValues, pendingCreateRows = [] } = response;

            const mappedPending = (pendingCreateRows || []).map((item) => ({
                ...item,
                id: item.pendingChangeId,
            }));

            // Map _id to id for consistency in frontend
            const mappedData = (data || []).map(item => ({
                ...item,
                id: item._id || item.id
            }));

            store.setCorrectiveActions([...mappedPending, ...mappedData]);

            if (pagination && Object.keys(pagination).length > 0) {
                store.setPaginationData({
                    ...pagination,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {{ pageKey?: string }} [listContext]
     */
    async createCorrectiveAction(data, listContext = {}) {
        const store = useCorrectiveActionStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const payload = this._cleanPayload({ ...data, actionType: data.actionType || 'corrective' });
            const svcOpts = listContext?.pageKey ? { pageKey: listContext.pageKey } : {};
            const response = await this.service.create(payload, svcOpts);

            if (this._isPendingApproval(response)) {
                toast.info(i18next.t('integrationToasts:changeSubmittedToApprovalWorkflow', { defaultValue: 'Change submitted to approval workflow' }), {
                    description: i18next.t('riskTreatment:actionsForm.createFlow.postCreateHint', { defaultValue: 'Edit the record for owner, department, schedule, and CAPA.' }),
                });
                if (store.closeForm) store.closeForm();
                return {
                    success: true,
                    pendingApproval: true,
                    pendingChangeId: response.pendingChangeId,
                    merged: !!response.merged,
                    draftExecutionTicketId: response.draftExecutionTicketId
                };
            }

            toast.success(i18next.t('integrationToasts:actionCreated', { defaultValue: 'Action created' }), {
                description: i18next.t('riskTreatment:actionsForm.createFlow.postCreateHint', { defaultValue: 'Edit the record for owner, department, schedule, and CAPA.' }),
            });

            if (store.closeForm) store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {{ pageKey?: string }} [listContext]
     */
    async updateCorrectiveAction(id, data, listContext = {}) {
        const store = useCorrectiveActionStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const payload = this._cleanPayload(data);
            const svcOpts = listContext?.pageKey ? { pageKey: listContext.pageKey } : {};
            const response = await this.service.update(id, payload, svcOpts);

            if (this._isPendingApproval(response)) {
                toast.info(i18next.t('integrationToasts:changeSubmittedToApprovalWorkflow', { defaultValue: 'Change submitted to approval workflow' }));
                if (store.closeForm) store.closeForm();
                return {
                    success: true,
                    pendingApproval: true,
                    pendingChangeId: response.pendingChangeId,
                    merged: !!response.merged,
                    draftExecutionTicketId: response.draftExecutionTicketId
                };
            }

            toast.success(i18next.t('integrationToasts:actionUpdated', { defaultValue: 'Action updated' }));

            if (store.closeForm) store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {{ pageKey?: string }} [listContext]
     */
    async deleteCorrectiveAction(id, listContext = {}) {
        const store = useCorrectiveActionStore.getState();
        store.setLoading(true);

        try {
            const svcOpts = listContext?.pageKey ? { pageKey: listContext.pageKey } : {};
            const response = await this.service.delete(id, svcOpts);

            if (this._isPendingApproval(response)) {
                toast.info(i18next.t('integrationToasts:deleteRequestSubmittedToApprovalWorkflow', { defaultValue: 'Delete request submitted to approval workflow' }));
                if (store.closeDeleteDialog) store.closeDeleteDialog();
                return {
                    success: true,
                    pendingApproval: true,
                    pendingChangeId: response.pendingChangeId
                };
            }

            toast.success(i18next.t('integrationToasts:actionDeleted', { defaultValue: 'Action deleted' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {{ pageKey?: string }} [listContext]
     */
    async batchDeleteCorrectiveAction(ids, listContext = {}) {
        const store = useCorrectiveActionStore.getState();
        store.setLoading(true);

        try {
            const svcOpts = listContext?.pageKey ? { pageKey: listContext.pageKey } : {};
            const response = await this.service.bulkDelete(ids, svcOpts);

            toast.success(i18next.t('integrationToasts:selectedActionsDeleted', { defaultValue: 'Selected actions deleted' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _cleanPayload(data) {
        const payload = { ...data };
        if (!payload.owner) delete payload.owner;
        if (!payload.unit) delete payload.unit;
        if (!payload.verifiedBy) delete payload.verifiedBy;
        if (!String(payload.verifiedDate ?? "").trim()) delete payload.verifiedDate;
        if (!payload.startDate) delete payload.startDate;
        if (!payload.deadline) delete payload.deadline;
        delete payload.rootCause;
        if (Array.isArray(payload.rootCauseReasons)) {
            payload.rootCauseReasons = payload.rootCauseReasons
                .map((id) => String(id).trim())
                .filter((id) => id.length === 24);
        } else {
            delete payload.rootCauseReasons;
        }
        if (payload.actionType !== 'corrective') {
            delete payload.rootCauseReasons;
        }
        if (!String(payload.preventiveRationale || "").trim()) delete payload.preventiveRationale;
        if (!String(payload.improvementObjective || "").trim()) delete payload.improvementObjective;
        for (const k of ["sourceType", "sourceId", "sourceTitle"]) {
            if (!String(payload[k] ?? "").trim()) delete payload[k];
        }
        if (typeof payload.progress === "number" && Number.isNaN(payload.progress)) {
            payload.progress = 0;
        }
        if (typeof payload.budget === "number" && Number.isNaN(payload.budget)) {
            payload.budget = 0;
        }
        return payload;
    }

    _isPendingApproval(response) {
        return response?.pendingChangeId || response?.data?.pendingChangeId;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchActionPartyTemplate(params = {}) {
        try {
            const res = await this.service.getPartyTemplate(params);
            return { success: true, data: res.data ?? null };
        } catch {
            return { success: false, data: null };
        }
    }

    async fetchActionParty(actionId) {
        if (!actionId) return { success: false, data: null, partyTemplate: null };
        try {
            const res = await this.service.getParty(actionId);
            return {
                success: res.success !== false,
                data: res.data ?? null,
                partyTemplate: res.partyTemplate ?? null,
            };
        } catch {
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncActionParty(actionId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const res = await this.service.syncParty(actionId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18next.t('riskTreatment:ownership.saveSuccess', {
                            defaultValue: 'Ownership saved',
                        }),
                );
            }
            return { success: true, data: res.data };
        } catch (err) {
            const msg = this._getErrorMessage(err);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    notifyPendingApprovalLoadFailed() {
        toast.error(
            i18next.t('riskTreatment:table.pendingApproval.loadFailed', {
                defaultValue: 'Could not load pending approval record',
            }),
        );
    }

    notifyPendingApprovalNone() {
        toast.error(
            i18next.t('riskTreatment:table.pendingApproval.none', {
                defaultValue: 'Pending approval record not found',
            }),
        );
    }

    notifyPendingApprovalNoUpdate() {
        toast.error(
            i18next.t('riskTreatment:table.pendingApproval.noUpdatePending', {
                defaultValue: 'No pending update record',
            }),
        );
    }
}
